import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, MapPin, ExternalLink, Users, Clock, Tag, DollarSign, UserCheck, Ticket } from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows, breakpoints } from '../styles/theme';
import { Event } from '../types/api';
import { useTranslations } from '../locales/useI18n';
import {
  getVenueName,
  getLocationLabel,
  formatPrice,
  formatCapacity,
  formatAudienceLevel,
  cleanStringArray,
} from '../utils/eventFields';
import {
  compareEventLocalDates,
  formatEventLocalDateTime,
  formatEventLocalDuration,
  localCalendarDateOffset,
} from '../utils/eventDateTime';
import { buildEventPath } from '../utils/slug';
import { toAbsoluteUrl } from '../utils/externalUrl';
import { displayEntityType } from '../utils/entityTerminology';
import { useAnalytics } from '../hooks/useAnalytics';
import { EventBadges } from './events/EventBadges';
import EventEntityAvatars from './events/EventEntityAvatars';
import RememberButton from './events/RememberButton';

interface EventCardProps {
  event: Event;
  onClick?: () => void;
  showFullDescription?: boolean;
  /** Entity whose page this card is on — collapses its own relationship badge. */
  contextEntityId?: number | null;
}

const Card = styled.article`
  background: ${colors.white};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.xl};
  overflow: hidden;
  box-shadow: ${shadows.sm};
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.md};
    border-color: ${colors.primary[300]};
  }
  
  @media (max-width: ${breakpoints.sm}) {
    border-radius: ${borderRadius.lg};
  }
`;

const CardHeader = styled.div<{ $isPastEvent?: boolean }>`
  position: relative;
  background: ${props => props.$isPastEvent ? colors.gradients.pastEvent : colors.gradients.hero};
  color: ${colors.white};
  padding: ${spacing[4]} calc(${spacing[6]} + 52px) ${spacing[4]} ${spacing[6]};
  
  @media (max-width: ${breakpoints.sm}) {
    padding: ${spacing[3]} calc(${spacing[4]} + 52px) ${spacing[3]} ${spacing[4]};
  }
`;

const CardRememberButton = styled(RememberButton)`
  position: absolute;
  top: ${spacing[2]};
  right: ${spacing[2]};
  z-index: 1;
`;

const EventTitle = styled.h3`
  font-size: ${typography.fontSizes.lg};
  font-weight: ${typography.fontWeights.semibold};
  margin-bottom: ${spacing[2]};
  line-height: ${typography.lineHeights.tight};
  color: ${colors.white};
  cursor: pointer;
  transition: color 0.15s ease;
  
  &:hover {
    color: ${colors.primary[100]};
    text-decoration: underline;
  }
  
  @media (max-width: ${breakpoints.sm}) {
    font-size: ${typography.fontSizes.base};
  }
`;

const EventTitleLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid ${colors.white};
    outline-offset: 2px;
  }
`;

const EventMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
  font-size: ${typography.fontSizes.sm};
  opacity: 0.9;
  flex-wrap: wrap;
  
  @media (max-width: ${breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${spacing[2]};
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const VenuePlace = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[1]};
  font-size: ${typography.fontSizes.sm};
`;

const VenuePlaceText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: ${typography.lineHeights.tight};
`;

const VenuePrimary = styled.span`
  font-weight: ${typography.fontWeights.semibold};
`;

const VenueSecondary = styled.span`
  font-size: ${typography.fontSizes.xs};
  opacity: 0.85;
`;

const CardBody = styled.div`
  padding: ${spacing[6]};
  
  @media (max-width: ${breakpoints.sm}) {
    padding: ${spacing[4]};
  }
`;

const EventDescription = styled.p`
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};
  margin-bottom: ${spacing[4]};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: pointer;
  transition: color 0.15s ease;
  
  &:hover {
    color: ${colors.primary[600]};
  }
  
  &.full {
    display: block;
    -webkit-line-clamp: unset;
  }
`;

const EventDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[4]};
  
  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${spacing[3]};
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
`;

const DetailIcon = styled.div`
  color: ${colors.primary[500]};
  margin-top: 2px;
  flex-shrink: 0;
`;

const DetailContent = styled.div`
  flex: 1;
`;

const DetailLabel = styled.div`
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.xs};
  margin-bottom: ${spacing[1]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: ${typography.fontWeights.medium};
`;

const DetailValue = styled.div`
  color: ${colors.text.primary};
  font-weight: ${typography.fontWeights.medium};
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${spacing[4]};
  border-top: 1px solid ${colors.border.light};
`;

const VirtualBadge = styled.div`
  padding: ${spacing[1]} ${spacing[2]};
  background: ${colors.success[100]};
  color: ${colors.success[700]};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Chip = styled.span<{ $tone?: 'neutral' | 'price' | 'audience' | 'topic' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  background: ${(props) =>
    props.$tone === 'price'
      ? colors.success[50]
      : props.$tone === 'audience'
      ? colors.primary[50]
      : props.$tone === 'topic'
      ? colors.background.secondary
      : colors.background.secondary};
  color: ${(props) =>
    props.$tone === 'price'
      ? colors.success[700]
      : props.$tone === 'audience'
      ? colors.primary[700]
      : colors.text.secondary};
  border: 1px solid ${(props) =>
    props.$tone === 'price'
      ? colors.success[100]
      : props.$tone === 'audience'
      ? colors.primary[100]
      : colors.border.light};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};
`;

const SourceIconLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[1]} ${spacing[2]};
  background: ${colors.primary[50]};
  border-radius: ${borderRadius.sm};
  color: ${colors.primary[600]};
  transition: all 0.15s ease;
  font-size: ${typography.fontSizes.xs};
  gap: ${spacing[1]};
  
  &:hover {
    background: ${colors.primary[100]};
    color: ${colors.primary[700]};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[2]} ${spacing[3]};
  background: ${colors.primary[500]};
  color: ${colors.white};
  border: none;
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;
  transition: background-color 0.15s ease;
  
  &:hover {
    background: ${colors.primary[600]};
  }
  
  &:focus {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
  }
`;

export const EventCard: React.FC<EventCardProps> = ({ 
  event, 
  onClick, 
  showFullDescription = false,
  contextEntityId = null
}) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  
  const isPastEvent = compareEventLocalDates(event.endDate, localCalendarDateOffset(0)) === -1;
  const formattedStart = formatEventLocalDateTime(event.startDate, { locale: 'en-US' });
  const detailPath = buildEventPath(event);
  const externalEventUrl = toAbsoluteUrl(event.eventUrl, event.eventSources ?? []);
  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // If a custom onClick is provided, use it; otherwise navigate to event details
    if (onClick) {
      onClick();
    } else if (detailPath) {
      navigate(detailPath);
    }
  };

  // Event details always open in the same tab; only external links leave the app.
  const handleNavigateToDetails = (e: React.MouseEvent) => {
    // Let ctrl/cmd/shift/middle clicks use the anchor's native behaviour.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else if (detailPath) {
      navigate(detailPath);
    }
  };

  const handleExternalLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (externalEventUrl) {
      trackEvent({ category: 'event_detail', action: 'source_click', custom_parameters: { action_location: 'event_grid_card', source: 'canonical_event_page' } });
      window.open(externalEventUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    trackEvent({ category: 'event_detail', action: 'source_click', custom_parameters: { action_location: 'event_grid_card', source: 'source_listing' } });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const duration = formatEventLocalDuration(event.startDate, event.endDate);
  const { translations } = useTranslations();

  const locationLabel = getLocationLabel(event);
  const venueName = getVenueName(event.venue);
  const priceLabel = formatPrice(event.price, event.currency, event.isFree);
  const capacityLabel = formatCapacity(event.capacity, event.ticketsRemaining);
  const audienceLabel = formatAudienceLevel(event.audienceLevel);
  const topics = cleanStringArray(event.topics).slice(0, 3);
  const industries = cleanStringArray(event.industries).slice(0, 2);
  const speakerNames = cleanStringArray(event.speakerNames);
  const hasChips = !!(priceLabel || capacityLabel || audienceLabel || topics.length || industries.length);

  return (
    <Card
      onClick={handleCardClick}
      data-event-id={event.eventId}
      tabIndex={0}
      aria-label={`${event.title} - ${formattedStart}${locationLabel ? `, ${locationLabel}` : ''}`}
    >
      <CardHeader $isPastEvent={isPastEvent}>
        <CardRememberButton
          eventId={event.eventId}
          actionLocation="event_grid_card"
          appearance="inverse"
          compact
        />
        <EventTitle>
          <EventTitleLink href={detailPath || undefined} onClick={handleNavigateToDetails}>
            {event.title}
          </EventTitleLink>
        </EventTitle>
        <EventMeta>
          <MetaItem>
            <Calendar size={16} />
            {formattedStart}
          </MetaItem>
          {!event.isVirtual && (venueName || locationLabel) && (
            <VenuePlace>
              <MapPin size={16} style={{ marginTop: 2, flexShrink: 0 }} />
              <VenuePlaceText>
                <VenuePrimary>{venueName || locationLabel}</VenuePrimary>
                {venueName && locationLabel && (
                  <VenueSecondary>{locationLabel}</VenueSecondary>
                )}
              </VenuePlaceText>
            </VenuePlace>
          )}
          {!event.isVirtual && typeof event.distanceKm === 'number' && (
            <MetaItem>
              {event.distanceKm.toFixed(1)} km away
            </MetaItem>
          )}
          {event.isVirtual && (
            <MetaItem>
              <Users size={16} />
              Virtual Event
            </MetaItem>
          )}
        </EventMeta>
      </CardHeader>

      <CardBody>
        <EventEntityAvatars entities={event.entities} />
        <EventBadges event={event} contextEntityId={contextEntityId} />

        {event.description && (
          <EventDescription 
            className={showFullDescription ? 'full' : ''}
            onClick={handleNavigateToDetails}
          >
            {event.description}
          </EventDescription>
        )}

        {hasChips && (
          <ChipRow>
            {priceLabel && (
              <Chip $tone="price" title="Price">
                <DollarSign />
                {priceLabel}
              </Chip>
            )}
            {audienceLabel && (
              <Chip $tone="audience" title="Audience level">
                <UserCheck />
                {audienceLabel}
              </Chip>
            )}
            {capacityLabel && (
              <Chip title="Capacity">
                <Ticket />
                {capacityLabel}
              </Chip>
            )}
            {topics.map((t) => (
              <Chip key={`t-${t}`} $tone="topic" title="Topic">
                {t}
              </Chip>
            ))}
            {industries.map((i) => (
              <Chip key={`i-${i}`} $tone="topic" title="Industry">
                {i}
              </Chip>
            ))}
          </ChipRow>
        )}

        <EventDetails>
          {duration && (
            <DetailItem>
              <DetailIcon>
                <Clock size={16} />
              </DetailIcon>
              <DetailContent>
                <DetailLabel>{translations.events.duration}</DetailLabel>
                <DetailValue>{duration}</DetailValue>
              </DetailContent>
            </DetailItem>
          )}

          {!event.isVirtual && venueName && (
            <DetailItem>
              <DetailIcon>
                <MapPin size={16} />
              </DetailIcon>
              <DetailContent>
                <DetailLabel>{translations.events.venue}</DetailLabel>
                <DetailValue>{venueName}</DetailValue>
              </DetailContent>
            </DetailItem>
          )}

          {speakerNames.length > 0 && (
            <DetailItem>
              <DetailIcon>
                <Users size={16} />
              </DetailIcon>
              <DetailContent>
                <DetailLabel>Speakers</DetailLabel>
                <DetailValue>
                  {speakerNames.slice(0, 3).join(', ')}
                  {speakerNames.length > 3 && ` +${speakerNames.length - 3} more`}
                </DetailValue>
              </DetailContent>
            </DetailItem>
          )}

          {event.entity && (
            <DetailItem>
              <DetailIcon>
                <Users size={16} />
              </DetailIcon>
              <DetailContent>
                <DetailLabel>{translations.events.relatedTo}</DetailLabel>
                <DetailValue>{event.entity.name}</DetailValue>
              </DetailContent>
            </DetailItem>
          )}

          {event.entity?.type && (
            <DetailItem>
              <DetailIcon>
                <Tag size={16} />
              </DetailIcon>
              <DetailContent>
                <DetailLabel>{translations.events.category}</DetailLabel>
                <DetailValue>{displayEntityType(event.entity.type)}</DetailValue>
              </DetailContent>
            </DetailItem>
          )}
        </EventDetails>

        <CardFooter>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], flexWrap: 'wrap' }}>
            {event.isVirtual && <VirtualBadge>Virtual</VirtualBadge>}
            {event.eventSources && event.eventSources.length > 0 && (
              <>
                {event.eventSources.map((source, index) => (
                  <SourceIconLink
                    key={index}
                    href={source}
                    onClick={(e) => handleSourceClick(e, source)}
                    title={`${translations.events.eventSource} ${index + 1}`}
                  >
                    <ExternalLink />
                    <span>Source {index + 1}</span>
                  </SourceIconLink>
                ))}
              </>
            )}
          </div>
          
          {externalEventUrl && (
            <ActionButton onClick={handleExternalLinkClick}>
              <ExternalLink size={12} />
              View Event
            </ActionButton>
          )}
        </CardFooter>
      </CardBody>
    </Card>
  );
};

export default EventCard;
